import { logDebug, logError } from './utils/logger.js';

/**
 * PortfolioLoader Class - Table of Contents
 *
 * 1. Constructor
 *    - Initializes API parameters, DOM references, state variables, and observers.
 *    - Starts initial prefetching.
 *
 * 2. _setupObserver
 *    - Sets up main IntersectionObserver for infinite scroll.
 *    - Calls _setupPrefetchObservers for additional observation points.
 *
 * 3. _setupPrefetchObservers
 *    - Creates observation points at 25%, 50%, and 75% of loaded items.
 *    - Prefetches next page when these points are reached.
 *
 * 4. _handleIntersection
 *    - Handles intersection events to load more items or render prefetched data.
 *
 * 5. _showLoadingPlaceholder / _removeLoadingPlaceholder
 *    - Manages loading UI placeholders during data fetch.
 *
 * 6. _fetchPortfolioData
 *    - Fetches portfolio data from API.
 *    - Handles query parameters, error logging, and state updates.
 *
 * 7. _processData
 *    - Processes fetched data.
 *    - Updates state, renders items, manages prefetch queue, and handles end-of-data.
 *
 * 8. _createItemElement
 *    - Clones and populates portfolio card template with item data.
 *
 * 9. _renderItems
 *    - Renders portfolio items to the grid.
 *    - Sets up observation for infinite scroll.
 *
 * 10. _prefetchNextPage
 *     - Prefetches next page of data and manages prefetch queue.
 *
 * 11. _renderFromQueue
 *     - Renders items from the prefetch queue.
 *
 * 12. loadMoreItems
 *     - Fallback method to load more items if prefetch queue is empty.
 */

class PortfolioLoader {
    constructor(apiBaseUrl, gridContainerSelector, excludeSlugs, nonce, origin, perPage = 9) {
        this.apiBaseUrl = apiBaseUrl;
        this.gridContainer = document.querySelector(gridContainerSelector);
        this.excludeSlugs = excludeSlugs;
        this.nonce = nonce;
        this.origin = origin;
        this.perPage = perPage; // Default to 9 items per page to match server-side
        this.portfolioCardTemplate = document.getElementById('portfolio-card-template');
        
        // Calculate server-side loaded items (27 items at 9 per page = 3 pages)
        this.serverSidePages = 3;
        
        // Prefetching strategy variables
        this.dataQueue = [];             // In-memory queue for prefetched data
        this.prefetchInProgress = false; // Track if we're currently prefetching
        this.totalPages = null;          // Will be set when we get the first response
        this.isAggressivePrefetching = false; // Will be true for small datasets
        this.observationPoints = [];     // Store observation points for cleanup

        const urlParams = new URLSearchParams(window.location.search);

        this.initialParams = {
            filter: urlParams.get('filter') || null,
            search: urlParams.get('search') || null,
            sqf: urlParams.get('sqf') || null,
            cost: urlParams.get('cost') || null,
            order: urlParams.get('order') || null
        };

        if (!this.gridContainer) {
            console.error('PortfolioLoader not properly initialized.');
            logError({ message: 'PortfolioLoader not properly initialized', gridContainerSelector });
            this.hasMoreData = false;
            return;
        }

       
        // Pages 1-3 are loaded server-side (27 items at 9 per page), so we start from page 4
        this.currentPage = this.serverSidePages + 1; // Start from page 4
        this.isLoading = false;
        this.hasMoreData = true;
        this.observer = null;
        this._activePlaceholder = null;
        
        // Keep track of which pages we've loaded or queued
        // Initialize with the server-side loaded pages
        this.loadedPages = new Set();
        for (let i = 1; i <= this.serverSidePages; i++) {
            this.loadedPages.add(i);
        }
        this.queuedPages = new Set();
        
        console.log(`Portfolio loader initialized starting from page ${this.currentPage} (first ${this.serverSidePages} pages loaded server-side)`);

        this._setupObserver();
        this._prefetchNextPage(); // Start prefetching page 4


  

    }

    _setupObserver() {
        // Find all portfolio cards (links) in the grid
        let portfolioCards = Array.from(this.gridContainer.children).filter(child => 
            child.tagName === 'A' && child.classList.contains('portfolio-card')
        );
        
        console.log(`Found ${portfolioCards.length} portfolio cards for observation`);
        
        if (portfolioCards.length === 0) {
            console.warn('No portfolio cards found for observation');
            return;
        }
        
        // Calculate how many cards are displayed in a row (we need this for observation)
        // Find items that have the same top offset
        const firstItem = portfolioCards[0];
        const firstRowTop = firstItem.getBoundingClientRect().top;
        const itemsPerRow = portfolioCards.filter(card => 
            Math.abs(card.getBoundingClientRect().top - firstRowTop) < 5
        ).length;
        
        console.log(`Detected ${itemsPerRow} items per row in the grid`);
        
        // Choose the trigger point - observe the item at 2/3 of the last server-side row
        // For 3 server-side pages with 9 items per page, that's around item #24 (of 27)
        const triggerIndex = Math.min(
            Math.floor(this.serverSidePages * this.perPage * 0.9), 
            portfolioCards.length - 1
        );
        
        // Get the card to observe for infinite scroll
        let elementToObserve = portfolioCards[triggerIndex];
        
        // Create observer with larger margin to load earlier
        const options = {
            root: null,
            rootMargin: '300px 0px', // Start loading well before user reaches item
            threshold: 0.1 // Trigger when 10% of the item is visible
        };
        
        // Disconnect any existing observer
        if (this.observer) {
            this.observer.disconnect();
        }

        // Create new observer
        this.observer = new IntersectionObserver(this._handleIntersection.bind(this), options);
        
        console.log(`Setting up observer on last card:`, elementToObserve);
        
        // Start observing the last card
        this.observer.observe(elementToObserve);
        
        // Debug: Mark the observed element visibly in development
        if (process.env.NODE_ENV !== 'production') {
            elementToObserve.setAttribute('data-observed', 'true');
        }
    }


    _handleIntersection(entries, observer) {
        entries.forEach(entry => {
            console.log(entry, 'entry');
            
            if (entry.isIntersecting && this.hasMoreData && !this.isLoading) {
                observer.unobserve(entry.target);
                console.log('Observed item in view — showing more content...');
                
                // Check if we have prefetched data available
                if (this.dataQueue.length > 0) {
                    this._renderFromQueue();
                } else {
                    // Fall back to traditional loading if queue is empty
                    this.loadMoreItems();
                }
            }
        });
    }

    _setupPrefetchObservers() {
        // Clear any existing observation points
        this.observationPoints.forEach(point => {
            if (point.observer && point.element) {
                point.observer.unobserve(point.element);
            }
        });
        this.observationPoints = [];
        
        // Create observation points at 25%, 50% and 75% through the content
        const items = Array.from(this.gridContainer.querySelectorAll('.portfolio-card'));
        if (items.length < 4) return; // Not enough items to create meaningful observation points
        
        const observeAt = [
            Math.floor(items.length * 0.25), // 25% through content
            Math.floor(items.length * 0.5),  // 50% through content
            Math.floor(items.length * 0.75)  // 75% through content
        ];
        
        observeAt.forEach((index, i) => {
            if (index < items.length) {
                const element = items[index];
                const options = {
                    root: null,
                    rootMargin: '50px 0px',
                    threshold: 0.1
                };
                
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            // When this point is reached, prefetch next page if not already in queue
                            observer.unobserve(entry.target);
                            this._prefetchNextPage(i + 1); // Prefetch with priority based on position
                        }
                    });
                }, options);
                
                observer.observe(element);
                this.observationPoints.push({ observer, element });
                
                 
            }
        });
    }


    _showLoadingPlaceholder() {
        const placeholder = document.createElement('div');
        placeholder.className = 'portfolio-loading-placeholder';
        placeholder.style.height = '300px';
        this.gridContainer.appendChild(placeholder);
        this._activePlaceholder = placeholder;
    }

    _removeLoadingPlaceholder() {
        if (this._activePlaceholder) {
            this._activePlaceholder.remove();
            this._activePlaceholder = null;
        }
    }
    
    /**
     * Get the next sequential page that hasn't been loaded yet
     * @returns {number} The next page number to load
     */
    _getNextSequentialPage() {
        // Find the highest page number we've already loaded or queued
        const loadedPages = Array.from(this.loadedPages);
        const queuedPages = Array.from(this.queuedPages);
        
        const maxPage = Math.max(
            ...loadedPages, 
            ...queuedPages,
            this.serverSidePages  // We know first pages are server-side loaded
        );
        
        // Start looking for the next available page from currentPage
        let nextPage = this.currentPage;
        
        // Make sure we don't go backwards or duplicate pages
        if (nextPage <= maxPage) {
            nextPage = maxPage + 1;
        }
        
        // Check if this page is already loaded or queued
        while (this.loadedPages.has(nextPage) || this.queuedPages.has(nextPage)) {
            nextPage++;
        }
        
        console.log(`Next sequential page: ${nextPage} (current: ${this.currentPage}, server-side: 1-${this.serverSidePages}, client-loaded: ${loadedPages.filter(p => p > this.serverSidePages).join(', ')})`);
        return nextPage;
    }

    async _fetchPortfolioData(page = null, forPrefetch = false) {
        if (!forPrefetch && (this.isLoading || !this.hasMoreData)) return null;

        // Get the next sequential page if none specified
        const pageToFetch = page || this._getNextSequentialPage();
        
        // Prevent duplicate loading of pages
        if (this.loadedPages.has(pageToFetch) || this.queuedPages.has(pageToFetch)) {
            console.log(`Page ${pageToFetch} already loaded or queued, skipping.`);
            return null;
        }
        
        // Track this page as queued
        if (forPrefetch) {
            this.queuedPages.add(pageToFetch);
        }
        
        if (forPrefetch) {
            if (this.prefetchInProgress) return null;
            this.prefetchInProgress = true;
        } else {
            this.isLoading = true;
        }
        
        const url = new URL(this.apiBaseUrl);
        console.log(`Fetching page ${pageToFetch} (${forPrefetch ? 'prefetch' : 'direct load'})`);
        
        url.searchParams.append('paged', pageToFetch);
        url.searchParams.append('per_page', this.perPage);
        
        // Add exclude_slugs parameter if available
        if (this.excludeSlugs && this.excludeSlugs.length > 0) {
            url.searchParams.append('exclude_slugs', this.excludeSlugs.join(','));
        }
        
        // Get all the existing slugs in the grid to ensure no duplicates are loaded
        const existingSlugs = Array.from(this.gridContainer.querySelectorAll('.portfolio-card[data-slug]'))
            .map(card => card.getAttribute('data-slug'))
            .filter(Boolean);
            
        if (existingSlugs.length > 0) {
            url.searchParams.append('exclude_slugs', existingSlugs.join(','));
        }
        
        for (const [key, value] of Object.entries(this.initialParams)) {
            if (value !== null) {
                url.searchParams.append(key, value);
            }
        }

        try {
            
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-WP-Nonce': this.nonce,
                    'Origin': this.origin
                },
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();
            
            // Set total pages if we don't have it yet
            if (this.totalPages === null && data.total_pages) {
                this.totalPages = data.total_pages;
                
                // If small dataset (<=5 pages), switch to aggressive prefetching
                if (this.totalPages <= 5) {
                    this.isAggressivePrefetching = true;
                    
                }
            }
            
            if (forPrefetch) {
                this.prefetchInProgress = false;
            } else {
                this.isLoading = false;
            }
            
        
            
            return data;
        } catch (error) {
            console.error(`Error ${forPrefetch ? 'prefetching' : 'fetching'} portfolio data:`, error);
            logError({
                message: `Error ${forPrefetch ? 'prefetching' : 'fetching'} portfolio data`,
                error: error.message,
                url: url.toString(),
                page: pageToFetch
            });
            
            if (forPrefetch) {
                this.prefetchInProgress = false;
            } else {
                this.isLoading = false;
                this.hasMoreData = false;
                this.observer.disconnect();
            }
            
            return null;
        }
    }

    _processData(data, forPrefetch = false) {
        if (!data || !data.posts || data.posts.length === 0) {
            console.log('No more items to load.');
            this.hasMoreData = false;
            
            if (!forPrefetch) {
                this.observer.disconnect();
                document.querySelectorAll('.portfolio-loading-placeholder').forEach(el => el.remove());
            }
            
            return false;
        }
        
        // Record the current page from the API response
        const currentPageFromAPI = data.current_page || this.currentPage;
        
        if (forPrefetch) {
            // Store page number with the data for later reference
            data._pageNumber = currentPageFromAPI;
            
            // Add to queue instead of rendering immediately
            this.dataQueue.push(data);
            
            // Track this page as queued (not loaded yet)
            this.queuedPages.add(currentPageFromAPI);
            
            console.log(`Added page ${currentPageFromAPI} to prefetch queue. Queue now has ${this.dataQueue.length} pages.`);
            return true;
        } else {
            // Render immediately
            this._renderItems(data.posts);
            
            // Mark this page as loaded and remove from queued if it was there
            this.loadedPages.add(currentPageFromAPI);
            this.queuedPages.delete(currentPageFromAPI);
            
            // Set current page to the next sequential page
            this.currentPage = Math.max(...Array.from(this.loadedPages)) + 1;
            
            console.log(`Processed page ${currentPageFromAPI}. Next page will be ${this.currentPage}.`);
            console.log(`Loaded pages: ${Array.from(this.loadedPages).join(', ')}`);
            
            if (data.total_pages && this.currentPage > data.total_pages) {
                console.log('All pages loaded.');
                this.hasMoreData = false;
                this.observer.disconnect();
                // Clean up any remaining loading placeholders
                document.querySelectorAll('.portfolio-loading-placeholder').forEach(el => el.remove());
            } else {
                // Set up new observation points after adding content
                this._setupPrefetchObservers();
            }
            
            return true;
        }
    }

    _createItemElement(itemData) {
        // Check if we have the portfolio card template
        if (!this.portfolioCardTemplate) {
            logError({ message: 'Portfolio card template not found', itemData });
            return null;
        }
        
        // Clone the template content
        const templateContent = this.portfolioCardTemplate.content.cloneNode(true);
        const portfolioCard = templateContent.querySelector('.portfolio-card');
        
        if (!portfolioCard) {
            logError({ message: 'Portfolio card element not found in template', itemData });
            return null;
        }
        
        // Update the card with the new data
        portfolioCard.href = itemData.permalink;
        portfolioCard.setAttribute('data-slug', itemData.slug);
        portfolioCard.style.backgroundImage = `url('${itemData.image_url}')`;
        
        // Update the title
        const titleElement = portfolioCard.querySelector('.portfolio-title');
        if (titleElement) {
            titleElement.textContent = itemData.title;
        }
        
        // Update the location (city/state)
        const citySpan = portfolioCard.querySelector('.city-span');
        if (citySpan) {
            const cityState = [];
            if (itemData.city) cityState.push(itemData.city);
            if (itemData.state) cityState.push(itemData.state);
            citySpan.textContent = cityState.join(', ');
        }
        
        // Add animation class
        requestAnimationFrame(() => {
            portfolioCard.classList.add('loaded');
        });
        
        return portfolioCard;
    }

    _renderItems(itemsData) {

        console.log({
            itemsData
        });
        
        const fragment = document.createDocumentFragment();
        let lastObservableItem = null;

        itemsData.forEach(itemData => {
            const itemElement = this._createItemElement(itemData);
            if (itemElement) {
                fragment.appendChild(itemElement);
                if (itemData.observe) {
                    lastObservableItem = itemElement;
                }
            }
        });

        this._removeLoadingPlaceholder();
        this.gridContainer.appendChild(fragment);

        if (lastObservableItem && this.hasMoreData) {
            console.log(`Observing next: "${itemsData.find(p => p.observe)?.title || 'Unnamed'}"`);
            this.observer.observe(lastObservableItem);
        } else if (!lastObservableItem && this.hasMoreData) {
            console.warn('No observable items found for next trigger.');
        }
    }

    /**
     * Prefetch the next page of data to add to the queue
     * @param {number} priority - Higher number = higher priority (0-3)
     */
    async _prefetchNextPage(priority = 0) {
        if (!this.hasMoreData || this.prefetchInProgress) return;
        
        // Get the next sequential page number
        const nextPage = this._getNextSequentialPage();
        
        // Check if we've reached the end of pages
        if (this.totalPages !== null && nextPage > this.totalPages) {
            console.log(`Reached final page (${this.totalPages}), not prefetching more.`);
            return;
        }
        
        // Check if this page is already loaded or queued
        if (this.loadedPages.has(nextPage) || this.queuedPages.has(nextPage)) {
            console.log(`Page ${nextPage} already loaded or queued, skipping prefetch.`);
            
            // Try the next page instead
            setTimeout(() => {
                this._prefetchNextPage(priority);
            }, 100);
            return;
        }
        
        console.log(`Prefetching page ${nextPage} (priority: ${priority})`);
        
        // Fetch the data in the background
        const data = await this._fetchPortfolioData(nextPage, true);
        
        if (data) {
            const success = this._processData(data, true);
            
            console.log(`Prefetch of page ${nextPage} ${success ? 'successful' : 'failed'}`);
            console.log(`Queue now contains: ${this.dataQueue.map(d => d._pageNumber).join(', ')}`);
            
            // If this is an aggressive prefetch and we have more pages, queue up the next one
            if (success && this.dataQueue.length < 3) {
                setTimeout(() => {
                    this._prefetchNextPage(priority);
                }, 300); // Small delay between prefetches to avoid overwhelming the API
            }
        }
    }
    
    /**
     * Render items from the prefetch queue
     */
    _renderFromQueue() {
        if (this.dataQueue.length === 0) {
            // Fall back to regular loading if queue is empty
            this.loadMoreItems();
            return;
        }
        
        // Get the next data page from the queue
        const nextData = this.dataQueue.shift();
        const pageNumber = nextData._pageNumber || 'unknown';
        
        console.log(`Rendering queued page ${pageNumber} from prefetch queue`);
        
        // Don't show loading placeholder for instant rendering
        this._processData(nextData);
        
        // Remove this page from queued pages set
        if (pageNumber !== 'unknown') {
            this.queuedPages.delete(pageNumber);
        }
        
        // After rendering from queue, start prefetching next page if needed
        this._prefetchNextPage();
    }
    
    /**
     * Traditional loading method (fallback if prefetch queue is empty)
     */
    async loadMoreItems() {
        if (!this.hasMoreData || this.isLoading) return;
        
        // First check if we have prefetched data
        if (this.dataQueue.length > 0) {
            this._renderFromQueue();
            return;
        }

        // If no prefetched data, show loading and fetch
        this._showLoadingPlaceholder();
        
        // Small throttle to prevent rapid multiple requests
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const newData = await this._fetchPortfolioData();
        
        if (newData) {
            if (newData.out_of_posts) {
                this.hasMoreData = false;
                this.observer.disconnect();
            }
            
            this._processData(newData);
            
            // After successful load, try prefetching the next page
            this._prefetchNextPage();
        }
    }
}

export default PortfolioLoader;
